import axios, { AxiosResponse, AxiosPromise } from "axios";
import axiosRetry from "axios-retry";
import { RetryConfig } from "../retry-config";
import { getV2AxiosRequestConfig } from "us.helper/utility";
import { getApimBaseUrl } from "us.common/functions/getApimBaseUrl";

const errorInterceptor = (error: any) => {
  switch (error?.response?.status) {
    case 400:
      break;
    case 401:
      break;

    case 404:
      break;

    default:
  }

  if (error?.response?.status === 500) {
    // If the error has status code 429, retry the request
    return axios.request(error.config);
  }
  return error.response;
};

const responseInterceptor = (response: AxiosResponse) => {
  switch (response.status) {
    case 200:
      break;
    case 204:
      break;
    default:
  }

  return response;
};

const axiosInstance = axios.create({
  baseURL: getApimBaseUrl(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  transformResponse: [
    function (data) {
      let response;
      try {
        response = data ? JSON.parse(data) : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

axiosRetry(axiosInstance, RetryConfig);

// Set the auth token for any request
axiosInstance.interceptors.request.use(getV2AxiosRequestConfig);

axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

const AzureHttp = {
  post: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D
  ): AxiosPromise {
    return axiosInstance.post(`/${service}/v1/${endpoint}`, data);
  },
  put: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D
  ): AxiosPromise {
    return axiosInstance.put(`/${service}/v1/${endpoint}`, data);
  },
  get: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D
  ): AxiosPromise {
    return axiosInstance.get(`/${service}/v1/${endpoint}`, {
      params: data,
    });
  },
  getById: function (
    service: string,
    endpoint: string,
    id: number | string
  ): AxiosPromise {
    return axiosInstance.get(`/${service}/v1/${endpoint}/${id}`);
  },
  delete: function (
    service: string,
    endpoint: string,
    id: number | string
  ): AxiosPromise {
    return axiosInstance.delete(`/${service}/v1/${endpoint}/${id}`);
  },
  deleteWithParams: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D
  ): AxiosPromise {
    return axiosInstance.delete(`/${service}/v1/${endpoint}`, {
      params: data,
    });
  },
  patch: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D
  ): AxiosPromise {
    return axiosInstance.patch(`/${service}/v1/${endpoint}`, data);
  },
  patchById: function (
    service: string,
    endpoint: string,
    id: number | string
  ): AxiosPromise {
    return axiosInstance.patch(`/${service}/v1/${endpoint}/${id}`);
  },
};

export default AzureHttp;
